import React from 'react';
import { Link } from 'react-router-dom';
import Header from './shared/Header';
import Footer from './shared/Footer';
import { ArrowRight, ExternalLink } from 'lucide-react';

const WorkPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            Case studies
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            What we've shipped.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-xl mx-auto leading-relaxed">
            Production systems, not prototypes. Here's the work.
          </p>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 space-y-10">

        {/* ─── Case study 1: DataBackfill ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-blue-600 px-8 py-5 flex items-center justify-between">
            <span className="text-white text-xs font-semibold uppercase tracking-widest">
              SaaS
            </span>
            <span className="text-blue-200 text-xs font-medium">Live since 2024</span>
          </div>

          <div className="p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              DataBackfill Sync
            </h2>
            <p className="text-blue-600 font-medium mb-8">GA4 to BigQuery backfill SaaS</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Problem
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Historical GA4 data isn't available in BigQuery by default. Teams analyzing
                  long-term trends had no reliable path to get it there.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Solution
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Built a production SaaS that pulls historical GA4 data via the Data API and
                  writes it directly into a customer's BigQuery instance. No data stored on our
                  side.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Outcome
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  1,400+ successful syncs. 99.9% uptime. Hundreds of properties served. Still
                  running.
                </p>
              </div>
            </div>

            <div className="mb-10">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Stack
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Python', 'Flask', 'React', 'BigQuery', 'GA4 Data API', 'Stripe', 'Descope'].map(
                  (tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                    >
                      {tech}
                    </span>
                  )
                )}
              </div>
            </div>

            <Link
              to="/product"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700
                         text-white rounded-lg font-medium transition-colors text-sm"
            >
              See the product
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>

        {/* ─── Case study 2: Tempo ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-blue-600 px-8 py-5 flex items-center justify-between">
            <span className="text-white text-xs font-semibold uppercase tracking-widest">
              iOS App
            </span>
            <span className="text-blue-200 text-xs font-medium">Live on the App Store in 2026</span>
          </div>

          <div className="p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Tempo
            </h2>
            <p className="text-blue-600 font-medium mb-8">Cycle-aware fitness app for women</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Problem
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Most fitness apps ignore the physiological reality that women's readiness, energy,
                  and recovery capacity shift across their menstrual cycle. Generic training plans
                  produce worse outcomes and higher dropout.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Solution
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  An adaptive fitness coach that learns from daily check-ins — energy, sleep, mood,
                  recovery — and adjusts workout intensity using cycle-phase context. Built on SwiftUI
                  with HealthKit integration and RevenueCat-powered Tempo Pro tiers.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Outcome
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Live on the App Store as Tempo Cycle (iOS). 5.0/5 rating. Currently on version
                  1.04.
                </p>
              </div>
            </div>

            <div className="mb-10">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Stack
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Swift', 'SwiftUI', 'HealthKit', 'RevenueCat', 'Supabase'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <a
              href="https://apps.apple.com/app/tempo-cycle/id6758034296"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700
                         text-white rounded-lg font-medium transition-colors text-sm"
            >
              Download on the App Store
              <ExternalLink className="ml-2" size={16} />
            </a>
          </div>
        </div>

        {/* ─── Case study 3: AssetOS (in development) ─── */}
        <div className="bg-white rounded-2xl border border-blue-200 shadow-sm overflow-hidden">
          <div className="bg-blue-50 px-8 py-5 flex items-center justify-between border-b border-blue-100">
            <div className="flex items-center gap-3">
              <span className="text-blue-700 text-xs font-semibold uppercase tracking-widest">
                Current Build
              </span>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full uppercase tracking-wide">
                In Development
              </span>
            </div>
            <span className="text-blue-400 text-xs font-medium">Launching 2026</span>
          </div>

          <div className="p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              AssetOS
            </h2>
            <p className="text-blue-600 font-medium mb-8">AI-powered distressed asset management platform</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Problem
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Distressed asset portfolios — civil judgments, tax liens, defaulted loans — require
                  continuous tracking of statutory interest, expiration deadlines, and recovery
                  workflows. Most operators run this on spreadsheets and manual processes, which
                  doesn't scale and introduces errors on high-stakes legal deadlines.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Solution
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  A multi-tenant platform built on Google's Agent Development Kit. Specialized agents
                  handle interest calculation, deadline monitoring, and recovery workflow automation.
                  Tenant isolation is enforced at the data layer, not just at the application level.
                  The first edition focuses on Civil Judgments, with additional asset classes planned.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                  Outcome
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Architecture and core agents built. Civil Judgments Edition currently in active
                  development. Targeting production launch in 2026.
                </p>
              </div>
            </div>

            <div className="mb-10">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Stack
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Google ADK', 'Gemini', 'Firestore', 'Python', 'Pydantic', 'React'].map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <Link
              to="/contact"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700
                         text-white rounded-lg font-medium transition-colors text-sm"
            >
              Learn more
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>

      </div>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Want your project here?</h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            We're selective about what we take on. If it's the right fit, we'll ship it.
          </p>
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700
                       text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
          >
            Start a Project
            <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WorkPage;
